import { useQueryClient } from '@tanstack/react-query';
import { type ComponentProps, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useCurrentOrganisation } from '#pie/auth';
import { useToast } from '#pie/components/toast/ToastContext';
import {
  getGetContactDetailsIdQueryKey,
  useGetContactDetailsId,
  usePutContactDetailsId,
} from '#pie/internal/api/pie-bff';
import { DeleteOrganisationUserModal } from './DeleteOrganisationUserModal';
import { FormOrganisationUser } from './FormOrganisationUser';

export const UpdateOrganisationUser = () => {
  const userId = useParams()['userId'];

  if (!userId) {
    throw new Error('user id is missing');
  }

  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { t: tom } = useTranslation('organisationManagement');

  const { currentAccount } = useCurrentOrganisation();

  const { data: user, isLoading } = useGetContactDetailsId(userId);

  /* istanbul ignore next -- @preserve */
  if (!isLoading && !user?.id) {
    throw new Error('user does not exist');
  }

  const isCurrentUser = user?.id === currentAccount?.userId;
  const defaultValues = useMemo(
    () => ({
      ...user,
      phone: user?.phone || '',
      email: user?.email || '',
      authorizationRoles: user?.roles || [],
      teamIds: user?.teams.map((team) => team.id),
    }),
    [user],
  );

  const { mutate, isPending: isMutateLoading } = usePutContactDetailsId({
    mutation: {
      onError: async (error) => {
        const errorCode = error as { code: string };
        switch (errorCode?.code) {
          case 'Contact.ActiveExists': {
            addToast({
              message: tom('toast.error.active_exists.message'),
              title: tom('toast.error.active_exists.title'),
              type: 'error',
              error: error,
            });
            break;
          }
          default: {
            addToast({
              message: t('account.toast.error.message'),
              title: t('account.toast.error.title'),
              type: 'error',
              error: error,
            });
          }
        }
      },
      onSuccess: () => {
        addToast({
          message: t('account.toast.succes.message'),
          title: t('common.string.user_adjusted'),
          type: 'success',
        });
        queryClient.invalidateQueries({
          queryKey: getGetContactDetailsIdQueryKey(userId),
        });
      },
    },
  });

  const handleOnSubmit: ComponentProps<
    typeof FormOrganisationUser
  >['onSubmit'] = (data) => {
    mutate({ data, id: userId });
  };

  return (
    !isLoading && (
      <FormOrganisationUser
        onSubmit={handleOnSubmit}
        isLoading={isLoading || isMutateLoading}
        defaultValues={defaultValues}
        action={
          <DeleteOrganisationUserModal
            userId={userId}
            disabled={isCurrentUser}
          />
        }
      />
    )
  );
};
